<template>
  <div
    class="overlay-item"
    :class="{ 'is-selected': selected }"
    :style="{
      left: `${item.x}%`,
      top: `${item.y}%`,
      cursor: readonly ? 'default' : 'move',
      '--icon-size': `${effectiveIconSize}px`,
    }"
    :title="`${item.name} (${item.equipmentType})`"
    @mousedown.stop="onStartMove"
  >
    <!-- SVG equipment icon + delete button + resize handles + view-mode card -->
    <div class="item-icon-wrapper mb-2">
      <div class="item-icon" :class="{ 'item-icon--selected': selected }">
        <img
          v-if="equipmentSvg"
          :src="equipmentSvg"
          :alt="item.equipmentType"
          class="equipment-svg-icon"
          draggable="false"
        />
        <v-icon v-else :size="effectiveIconSize" color="primary">mdi-chip</v-icon>

        <!-- Resize handles — visible when selected in edit mode -->
        <template v-if="selected && !readonly">
          <div class="resize-handle resize-handle--nw" @mousedown.stop="onStartResize" />
          <div class="resize-handle resize-handle--ne" @mousedown.stop="onStartResize" />
          <div class="resize-handle resize-handle--sw" @mousedown.stop="onStartResize" />
          <div class="resize-handle resize-handle--se" @mousedown.stop="onStartResize" />
        </template>
      </div>

      <!-- Delete button (management only) -->
      <button
        v-if="!readonly"
        class="item-delete"
        title="Remove"
        @click.stop="emit('remove', item.id)"
      >
        ✕
      </button>
    </div>

    <!-- KPI card — view mode only -->
    <v-card v-if="readonly" class="readings-card" elevation="4" rounded="lg">
      <v-card-text class="pa-2">
        <template v-if="readings && readings.length > 0">
          <table class="kpi-table">
            <tbody>
              <tr v-for="r in readings" :key="r.readingName">
                <td class="kpi-col-value text-primary">{{ Number(r.readingValue).toFixed(1) }}</td>
                <td class="kpi-col-uom">{{ r.readingUoM }}</td>
              </tr>
            </tbody>
          </table>
        </template>
        <span v-else class="kpi-name text-medium-emphasis">{{ item.name }}</span>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { IOverlayItem, IEquipmentReading } from '../types';
import { getEquipmentSvg } from '../types';

const props = defineProps<{
  item: IOverlayItem;
  /** The overlay layer element – used to compute relative drop coordinates. */
  canvasRef: HTMLElement | null;
  /** Whether this item is currently selected for individual editing (blue ring). */
  selected?: boolean;
  /** When true, disables move and delete interactions (read-only viewer). */
  readonly?: boolean;
  /** Icon size in px — computed by BaseSvgCanvas as a fraction of canvas width. */
  iconSize?: number;
  /**
   * Latest readings for this equipment item — rendered as a table card in view mode.
   */
  readings?: IEquipmentReading[];
}>();

const emit = defineEmits<{
  remove: [id: string];
  move: [payload: { id: string; x: number; y: number }];
  /** Fired once at drag start so the parent can snapshot state before any movement. */
  moveStart: [id: string];
  /** Fired when the drag ends (mouseup) so the parent can clear snap guides. */
  moveEnd: [id: string];
  /** multi=true when Ctrl/Cmd was held during the click */
  select: [id: string, multi: boolean];
  /** Fired once at resize start so the parent can snapshot state. */
  resizeStart: [id: string];
  /** Fired continuously while resizing with the new scale value. */
  resize: [payload: { id: string; scale: number }];
}>();

const equipmentSvg = computed(() => getEquipmentSvg(props.item.equipmentType));

/** Effective icon size in px: global iconSize multiplied by the item's individual scale. */
const effectiveIconSize = computed(() =>
  (props.iconSize ?? 96) * (props.item.scale ?? 1)
);

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

const onStartMove = (e: MouseEvent): void => {
  if (props.readonly) return;
  emit('select', props.item.id, e.ctrlKey || e.metaKey);
  const canvas = props.canvasRef;
  if (!canvas) return;

  emit('moveStart', props.item.id);

  const rect = canvas.getBoundingClientRect();

  const onMouseMove = (me: MouseEvent): void => {
    const x = clamp(((me.clientX - rect.left) / rect.width) * 100, 0, 100);
    const y = clamp(((me.clientY - rect.top) / rect.height) * 100, 0, 100);
    emit('move', { id: props.item.id, x, y });
  };

  const onMouseUp = (): void => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    emit('moveEnd', props.item.id);
  };

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
};

const onStartResize = (e: MouseEvent): void => {
  if (props.readonly) return;
  const canvas = props.canvasRef;
  if (!canvas) return;

  emit('resizeStart', props.item.id);

  const rect = canvas.getBoundingClientRect();
  const startScale = props.item.scale ?? 1;

  // Item center in screen pixels — used to compute distance-based scale ratio.
  const cx = rect.left + (props.item.x / 100) * rect.width;
  const cy = rect.top + (props.item.y / 100) * rect.height;
  const startDist = Math.sqrt((e.clientX - cx) ** 2 + (e.clientY - cy) ** 2);

  if (startDist < 1) return;

  const onMouseMove = (me: MouseEvent): void => {
    const d = Math.sqrt((me.clientX - cx) ** 2 + (me.clientY - cy) ** 2);
    const newScale = clamp((startScale * d) / startDist, 0.3, 4);
    emit('resize', { id: props.item.id, scale: newScale });
  };

  const onMouseUp = (): void => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
};
</script>

<style lang="scss" scoped>
.overlay-item {
  position: absolute;
  // Only the icon drives the bounding box — card is absolute so it doesn't affect sizing.
  // translate(-50%, -50%) therefore always centers the icon at (x, y) regardless of card height.
  transform: translate(-50%, -50%);
  display: inline-block;
  cursor: move;
  user-select: none;
  z-index: 10;

  &:hover .item-delete {
    display: flex;
  }
}

.item-icon-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
}

.item-icon {
  width: var(--icon-size, 96px);
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.35));
  z-index: 9999;

  &--selected {
    outline: 2.5px dashed #1565c0;
    outline-offset: 3px;
  }
}

.resize-handle {
  position: absolute;
  width: 10px;
  height: 10px;
  background: white;
  border: 2px solid #1565c0;
  border-radius: 2px;
  z-index: 30;

  &--nw { top: -5px; left: -5px; cursor: nw-resize; }
  &--ne { top: -5px; right: -5px; cursor: ne-resize; }
  &--sw { bottom: -5px; left: -5px; cursor: sw-resize; }
  &--se { bottom: -5px; right: -5px; cursor: se-resize; }
}

.equipment-svg-icon {
  width: var(--icon-size, 96px);
  height: auto;
  max-height: calc(var(--icon-size, 96px) * 1.4);
  display: block;
}

.item-delete {
  display: none;
  position: absolute;
  top: -15px;
  right: -30px;
  width: 33px;
  height: 33px;
  border-radius: 50%;
  background: #d32f2f;
  border: none;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  z-index: 20;
  padding: 0;
  font-size: 17px;
  font-weight: 700;
  color: white;
  line-height: 1;
}

/* ── KPI card — absolutely positioned below the icon so it never affects icon placement ── */
.readings-card {
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  min-width: clamp(140px, calc(var(--icon-size, 96px) * 1.5), 220px);
  background: rgba(255, 255, 255, 0.96);
  white-space: nowrap;
}

.kpi-name {
  font-size: clamp(13px, calc(var(--icon-size, 96px) * 0.17), 24px);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kpi-table {
  width: 100%;
  border-collapse: collapse;

  td {
    padding: clamp(1px, calc(var(--icon-size, 96px) * 0.02), 4px) 3px;
    vertical-align: baseline;
    line-height: 1.3;
  }
}

.kpi-col-uom {
  font-size: clamp(13px, calc(var(--icon-size, 96px) * 0.19), 32px);
  font-weight: 500;
  text-align: left;
  white-space: nowrap;
  color: #546e7a;
  padding-left: clamp(8px, calc(var(--icon-size, 96px) * 0.12), 20px);
}

.kpi-col-value {
  font-size: clamp(15px, calc(var(--icon-size, 96px) * 0.23), 38px);
  font-weight: 700;
  text-align: right;
  white-space: nowrap;
}
</style>
